import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Center,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Heading,
  Alert,
  AlertIcon,
  Link,
  Spinner,
} from '@chakra-ui/react';
import { formatNameLastCommaFirst } from '../utils/clientNameDisplay';
import { getClientDisplayAge } from '../utils/clientAge';
import { formatMakeupBadgeDate } from '../utils/attendanceDisplay';

const SESSION_TYPE_LABELS = {
  IE: 'Initial Evaluation',
  Endorsement: 'Endorsement',
  Makeup: 'Makeup',
};

const SESSION_TYPE_COLORS = {
  IE: 'purple',
  Endorsement: 'teal',
  Makeup: 'blue',
};

const getReminderType = (session) => {
  if (session.is_makeup) return 'Makeup';
  return session.session_type;
};

const NewClientRemindersView = ({
  sessions = [],
  loading = false,
  targetDate,
  isTomorrow = false,
  formatTime,
}) => {
  const formattedDate = targetDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  if (loading) {
    return (
      <Center py={12}>
        <Spinner size="lg" />
      </Center>
    );
  }

  return (
    <VStack align="stretch" spacing={4}>
      <HStack justify="space-between" flexWrap="wrap" gap={2}>
        <Heading size="md">Schedule Reminders</Heading>
        <Badge colorScheme={sessions.length > 0 ? 'orange' : 'gray'} fontSize="md" px={3} py={1}>
          {sessions.length} scheduled
        </Badge>
      </HStack>

      {isTomorrow ? (
        <Box
          px={4}
          py={3}
          borderRadius="md"
          borderWidth="2px"
          borderColor="orange.300"
          bg="orange.50"
        >
          <HStack spacing={3} flexWrap="wrap" align="center">
            <Badge colorScheme="orange" fontSize="sm" px={3} py={1} textTransform="uppercase">
              Tomorrow
            </Badge>
            <Text fontSize="lg" fontWeight="bold" color="orange.900">
              {formattedDate}
            </Text>
          </HStack>
          <Text fontSize="sm" color="orange.800" mt={2}>
            Reminders below are for sessions scheduled tomorrow — not today.
          </Text>
        </Box>
      ) : (
        <Text fontSize="sm" color="gray.600">
          Selected day — {formattedDate}
        </Text>
      )}

      {sessions.length > 0 ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            Send schedule reminders to guardians for IE, Endorsement, and makeup sessions before their appointment.
          </Text>
        </Alert>
      ) : (
        <Center py={10}>
          <Text color="gray.500">
            No IE, Endorsement, or makeup sessions scheduled for {formattedDate}
          </Text>
        </Center>
      )}

      {sessions.length > 0 && (
        <Box borderWidth={1} borderRadius="md" overflow="hidden" bg="white">
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead bg="orange.50">
                <Tr>
                  <Th>Time</Th>
                  <Th>Client</Th>
                  <Th>Type</Th>
                  <Th>Therapist</Th>
                  <Th>Guardian</Th>
                  <Th>Contact</Th>
                  <Th>Details</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sessions.map((session) => {
                  const client = session.client || { name: session.client_name };
                  const contact = client.guardian_contact;
                  const displayAge = getClientDisplayAge(client);
                  const reminderType = getReminderType(session);

                  return (
                    <Tr key={session.id}>
                      <Td whiteSpace="nowrap" fontWeight="medium">
                        {formatTime(session.time)}
                      </Td>
                      <Td>
                        <Text fontSize="sm" fontWeight="medium">
                          {formatNameLastCommaFirst(client.name)}
                        </Text>
                        {client.nickname && (
                          <Text fontSize="xs" color="gray.500">
                            {client.nickname}
                          </Text>
                        )}
                      </Td>
                      <Td whiteSpace="nowrap">
                        <Badge colorScheme={SESSION_TYPE_COLORS[reminderType] || 'gray'}>
                          {SESSION_TYPE_LABELS[reminderType] || reminderType}
                        </Badge>
                      </Td>
                      <Td>
                        <Text fontSize="sm">{session.therapist?.name}</Text>
                        {session.therapist?.nickname && (
                          <Text fontSize="xs" color="gray.500">
                            {session.therapist.nickname}
                          </Text>
                        )}
                        <Text fontSize="xs" color="gray.500">
                          {session.therapist?.specialization}
                        </Text>
                      </Td>
                      <Td whiteSpace="nowrap">{client.guardian_name || '—'}</Td>
                      <Td whiteSpace="nowrap">
                        {contact ? (
                          <Link href={`tel:${contact}`} color="blue.600" fontSize="sm">
                            {contact}
                          </Link>
                        ) : (
                          <Text fontSize="sm" color="red.500">
                            No contact
                          </Text>
                        )}
                      </Td>
                      <Td>
                        {displayAge != null && (
                          <Text fontSize="xs" color="gray.600">
                            {displayAge} y/o
                          </Text>
                        )}
                        {client.diagnosis && (
                          <Text fontSize="xs" color="gray.600" noOfLines={2}>
                            {client.diagnosis}
                          </Text>
                        )}
                        {session.is_makeup && session.makeup_for && (
                          <Text fontSize="xs" color="blue.600">
                            For {formatMakeupBadgeDate(session.makeup_for)}
                          </Text>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        </Box>
      )}
    </VStack>
  );
};

export default NewClientRemindersView;
